import type { ComponentPropsWithRef } from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '../../lib/cn.ts';

/* The six deep tones of the avatar palette, in a fixed order the hash indexes. */
export const AVATAR_TONES = ['moss', 'navy', 'teal', 'plum', 'berry', 'ochre'] as const;

export type AvatarTone = (typeof AVATAR_TONES)[number];

const avatarVariants = cva(
  'flex shrink-0 items-center justify-center rounded-full font-bold text-white',
  {
    variants: {
      tone: {
        moss: 'bg-avatar-moss',
        navy: 'bg-avatar-navy',
        teal: 'bg-avatar-teal',
        plum: 'bg-avatar-plum',
        berry: 'bg-avatar-berry',
        ochre: 'bg-avatar-ochre',
      },
      size: {
        sm: 'size-8 text-sm',
        md: 'size-11.5 text-md',
        /* The person a page is about: their header, where the name stands beside it in display size. */
        lg: 'size-16 text-xl',
      },
    },
    defaultVariants: {
      tone: 'teal',
      size: 'md',
    },
  },
);

export type AvatarVariants = VariantProps<typeof avatarVariants>;

/**
 * Which of the six tones a name gets.
 *
 * A hash rather than a counter, so the same person is the same colour on every
 * page, in every order, forever — a list that recolours itself when it is
 * re-sorted is a list you cannot scan.
 */
export function avatarTone(seed: string): AvatarTone {
  let hash = 0;
  for (const character of seed.trim().toLowerCase()) {
    hash = (hash * 31 + character.codePointAt(0)!) % 1_000_003;
  }
  return AVATAR_TONES[hash % AVATAR_TONES.length];
}

/**
 * The two letters that stand for a name.
 *
 * The first letter of each of the first two words, which for "Charlotte De Vos"
 * is CD — the reading order, not a guess at which word is the family name.
 * Dutch names carry tussenvoegsels and hyphens, so "the last word" is the wrong
 * half as often as it is the right one.
 *
 * A name of one word gives its first two letters rather than one lonely
 * capital, and an address falls back to the part in front of the @, because
 * "AN" says more than "@" does.
 */
export function avatarInitials(name: string): string {
  const source = name.includes('@') ? name.split('@')[0] : name;
  const words = source.split(/[\s._-]+/).filter(Boolean);
  if (words.length === 0) return '?';

  const letters =
    words.length === 1 ? [...words[0]].slice(0, 2) : [[...words[0]][0], [...words[1]][0]];
  return letters.join('').toUpperCase();
}

export interface AvatarProps
  extends
    Omit<ComponentPropsWithRef<'span'>, 'style' | 'children'>,
    Omit<AvatarVariants, 'tone'> {
  /** The name to abbreviate. An address will do when there is no name. */
  name: string;
  /**
   * What the colour is hashed from. Pass the address: a person who changes
   * their display name should not change colour with it.
   */
  seed?: string;
  /**
   * Their picture, when we have one. Rendered straight from the URL with no
   * fetch of ours: a profile-image URL names the version it serves and is
   * cached indefinitely, so the browser asks once and a new picture is simply
   * a new URL.
   *
   * Null and undefined both mean "no picture", which is the ordinary case — a
   * colleague has one only if they use Wundo themselves.
   */
  src?: string | null;
}

/**
 * A person, as a disc: their picture when we have one, their initials when we
 * do not.
 *
 * The tone is hashed either way, so it is already right underneath a photo that
 * is still loading and stays right for a photo that never arrives.
 *
 * `aria-hidden`, always: the name it stands for sits right beside it, and a
 * screen reader reading "CD" before "Charlotte De Vos" is noise. That is also
 * why the image needs no alt text of its own.
 */
export function Avatar({ className, name, seed, size, src, ...props }: AvatarProps) {
  return (
    <span
      data-slot='avatar'
      aria-hidden='true'
      className={cn(
        avatarVariants({ tone: avatarTone(seed ?? name), size }),
        /* Only with a picture in it: a disc of initials has nothing to clip. */
        src && 'overflow-hidden',
        className,
      )}
      {...props}
    >
      {src ? (
        <img src={src} alt='' className='size-full object-cover' />
      ) : (
        avatarInitials(name)
      )}
    </span>
  );
}

export { avatarVariants };
